// Updated OrderAPIs.js
import { getToken } from "../utils/auth";
import { API_BASE_URL } from './URL_Header';


export const fetchOrderById = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetOrder/${id}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchOrderById failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch order");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const createOrder = async (order) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/MakeOrder`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(order),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`createOrder failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to create order");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const updateOrder = async (id, order) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/EditOrder/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(order),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`updateOrder failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to update order");
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const deleteOrder = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/DeleteOrder/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`deleteOrder failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to delete order");
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};
//-----------------------------Unified Order APIs---------------------
const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
});

const buildOrdersUrl = (status, page, pageSize, date) => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (status) params.set("status", status);
  if (date) params.set("date", date);
  return `${API_BASE_URL}/Order/Orders?${params.toString()}`;
};

/** Unified paginated order list — replaces all GetAll*Orders endpoints */
export const fetchOrders = async (status, page = 0, pageSize = 10, date = null) => {
  try {
    const response = await fetch(buildOrdersUrl(status, page, pageSize, date), {
      headers: authHeaders(),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchOrders(${status}) failed: ${response.status} - ${errorText}`);
      throw new Error(`Failed to fetch ${status || "all"} orders`);
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

/** Unified summary — replaces all Get*OrdersSummary endpoints */
export const fetchOrdersSummary = async (status = null) => {
  try {
    const url = status
      ? `${API_BASE_URL}/Order/Summary?status=${encodeURIComponent(status)}`
      : `${API_BASE_URL}/Order/Summary`;
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchOrdersSummary(${status}) failed: ${response.status} - ${errorText}`);
      throw new Error("Failed to fetch orders summary");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

//-----------------------------Status wrappers (backward-compatible)---------------------
export const fetchNewOrders = (page = 0, pageSize = 10) =>
  fetchOrders("New", page, pageSize);

export const fetchHoldOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Hold", page, pageSize);

export const fetchChangesOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Changes", page, pageSize);

export const fetchOldOrders = (page = 0, pageSize = 10, date = null) =>
  fetchOrders("Old", page, pageSize, date);

export const fetchSpamOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Spam", page, pageSize);

export const fetchConfirmedOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Confirmed", page, pageSize);

export const fetchGirabatcomOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Girabatcom", page, pageSize);

//-----------------------------Purchase wrappers---------------------
export const fetchUnderPurchaseOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Under Purchase", page, pageSize);

export const fetchWaitingConfirmingOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Waiting Confirming", page, pageSize);

export const fetchCanceledOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Canceled", page, pageSize);

export const fetchCancelledByCustomerOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Cancelled by Customer", page, pageSize);

export const fetchConfirmingOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Confirming", page, pageSize);

export const fetchDepositRequiredOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Deposit", page, pageSize);

export const fetchCustomOrderRequests = (page = 0, pageSize = 10) =>
  fetchOrders("Custom", page, pageSize);
//------------------------------Totals & Summaries--------------------
export const fetchUniqueOldDates = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetUniqueOldDates`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchUniqueOldDates failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch unique old dates");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchNewOrdersTotals = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetNewOrdersTotals`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchNewOrdersTotals failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch new orders totals");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchHoldOrdersTotals = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetHoldOrdersTotals`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchHoldOrdersTotals failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch hold orders totals");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchChangesOrdersTotals = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetChangesOrdersTotals`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchChangesOrdersTotals failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch changes orders totals");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchOldOrdersTotals = async (date = null) => {
  try {
    let url = `${API_BASE_URL}/Order/GetOldOrdersTotals`;
    if (date) {
      url += `?date=${encodeURIComponent(date)}`;
    }
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchOldOrdersTotals failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch old orders totals");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchNewOrdersSummary = () => fetchOrdersSummary("New");
export const fetchConfirmedOrdersSummary = () => fetchOrdersSummary("Confirmed");
export const fetchGirabatcomOrdersSummary = () => fetchOrdersSummary("Girabatcom");
export const fetchCanceledOrdersSummary = () => fetchOrdersSummary("Canceled");
export const fetchHoldOrdersSummary = () => fetchOrdersSummary("Hold");
export const fetchChangesOrdersSummary = () => fetchOrdersSummary("Changes");
export const fetchOldOrdersSummary = () => fetchOrdersSummary("Old");
export const fetchDepositRequiredSummary = () => fetchOrdersSummary("Deposit");
export const fetchCustomOrdersSummary = () => fetchOrdersSummary("Custom");

export const deleteAllNewOrders = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/DeleteAllNewOrders`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`deleteAllNewOrders failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to delete all new orders");
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const deleteAllOldOrders = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/DeleteAllOldOrders`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`deleteAllOldOrders failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to delete all old orders");
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

// api/OrderAPIs.js
export const updateOrderStatus = async (id, status, reason = null, fullNote = null) => {
  try {
    const payload = { status };
    
    // إذا كان هناك سبب، نضيفه
    if (reason && reason.trim()) {
      payload.reason = reason.trim();
    }

    // إذا كان هناك note كاملة، نرسلها
    if (fullNote !== null) {
      payload.note = fullNote;
    }

    const response = await fetch(
      `${API_BASE_URL}/Order/UpdateOrderStatus/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`updateOrderStatus failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("فشل تحديث حالة الطلب");
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message || "حدث خطأ أثناء التحديث");
  }
};

export const revertToNew = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/RevertToNew/${id}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`revertToNew failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to revert order to new");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const searchOrdersByPhone = async (phone) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/SearchOrdersByPhone?phone=${encodeURIComponent(phone)}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`searchOrdersByPhone failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to search orders by phone");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const checkDuplicatePhoneCount = async (currentOrderId, phone1, phone2) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/CheckDuplicatePhoneCount/${currentOrderId}?` +
      new URLSearchParams({
        phone1: phone1 || "",
        phone2: phone2 || "",
      }),
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("فشل في التحقق من تكرار الرقم");
    }

    return await response.json(); // يرجع عدد الطلبات
  } catch (err) {
    throw new Error(err.message);
  }
};

export const processFollowUpPhones = async (phoneNumbers) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/ProcessFollowUpPhones`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ phoneNumbers }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to process follow-up phones");
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};
